/// <reference path="../../../node_modules/@1js/office-online-strings/en-US/transcription-strings.d.ts" />

import Dispatcher from '../../frontend/dispatcher';
import { TaskListResultStatus, TaskListResult, TaskList } from '../transcriptionManager/TaskList';
import { IAuthenticationProvider } from '../interfaces/IAuthenticationProvider';
import { LoadTranscriptionSourceFileToStoreAndNavigateToTranscribingPage } from '../../frontend/actions/LoadingActions';
import { LiveRecordingSession } from '../common/liveRecording/LiveRecordingSession';
import { OneDriveUploadTask, IOneDriveUploadTaskResult } from "../oneDrive/OneDriveUploadTask";
import { ITranscription } from "../interfaces/ITranscriptionManager";
import { IStoredTranscription } from "../interfaces/IStoredTranscription";
import * as AudioFormat from "../interfaces/IAudioFormat";
import { LogEvents } from "../../utils/LogEvents";
import { LiveRecordingKeys } from "../telemetry/LiveRecordingKeys";
import { IForegroundTask } from '../interfaces/IForegroundTask';
import { ErrorStringFromException } from '../../utils/utils';
import { ITranscriptionSourceFileProvider } from '../interfaces/ITranscriptionSourceFileProvider';
import { CreateTranscriptionSourceFileProvider } from '../factory/TranscriptionSourceFileFactory';
import { IsConnectedToNetwork } from '../../frontend/NetworkConnectionMonitor';
import { LogError, ShouldLogErrorAsWarning } from '../../utils/ErrorTelemetry';
import { LogEventNames } from '../interfaces/ITelemetryLogger';
import { TranscriptionTelemetryErrorCodes } from '../../utils/TranscriptionTelemetryErrorCodes';
import { RecordingEventManager } from '../manager/RecordingEventManager/RecordingEventManager';
import { GetSupportedTranscriptionStorageType, HostAppSettingName, HostAppSettings } from '../office-js/HostAppSettings';
import { TranscriptionManagerFactory } from '../factory/TranscriptionManagerFactory';
import {LoadingActionTypes, LoadingStatus} from '../../frontend/common/enums/loadingEnums';
import { SetForegroundTaskInStore } from '../../frontend/common/actions/LoadingActions';
import { CreateAddTranscriptionIndicatorProvider } from '../factory/AddTranscriptionIndicatorToHostFactory';
import { IAddTranscriptionIndicatorToHost } from '../interfaces/IAddTranscriptionIndicatorToHost';
import { ITranscriptionErrorEvent } from '../interfaces/ITranscriptionErrorEvent';
import { LOAD_TRANSCRIPTION_STRINGS_FAILURE_MESSAGE } from '../../frontend/LoadTranscriptionStrings';
import { FailureContinuation } from '../../frontend/common/enums/failureEnums';
import { SetFailureStateAndRouteToFailure } from '../../frontend/actions/FailureActions';
import { TranscribingEntryPoints } from '../../AppConstants';

export class LiveRecordingForegroundTask implements IForegroundTask {
    private liveRecordingSession: LiveRecordingSession;
    private history: any;
    private session: LiveRecordingSession | null=null;
    private taskList: TaskList<any> | null = null;
    private oneDriveUploadTask: OneDriveUploadTask | null=null;
    private authProvider: IAuthenticationProvider;
    private isCancelledTask: boolean = false;

    constructor(liveRecordingSession: LiveRecordingSession, authProvider: IAuthenticationProvider, history: any) {
      this.liveRecordingSession = liveRecordingSession;
      this.history = history;
      this.authProvider = authProvider;
    }

    public async Execute(): Promise<void> {
        try {
            this.isCancelledTask = false;
            Dispatcher.dispatch({
                type: LoadingActionTypes.SetStatusAction,
                payload: LoadingStatus.UploadingFile
            });

            await this.UploadFileAndInitiateTranscription(this.liveRecordingSession, this.history);
        } catch (e: any) {
            await this.CancelAndClearBackup(false /*shouldClearBackup*/);

            if (!IsConnectedToNetwork()) {
              LogError(LogEventNames.Warning, "Error uploading live recording, is cancelled Task (" + this.isCancelledTask + ")" + TranscriptionTelemetryErrorCodes.ConnectionIssueFailureReason);
            }

            const failureReason = "Error uploading live recording, is cancelled Task (" + this.isCancelledTask + ")" + ErrorStringFromException(e);
            LogEvents.LiveRecording.End([{ key: LiveRecordingKeys.FailureReason, value: failureReason }, { key: LiveRecordingKeys.SuccessfullyEnded, value: false }]);
            if (ShouldLogErrorAsWarning(e)) {
              LogEvents.ServiceLiveSpeechToText.AddResponse({ isSuccessful: true, message: failureReason, isFinalResponse: true });
            } else {
              LogEvents.ServiceLiveSpeechToText.AddResponse({ isSuccessful: false, message: failureReason, isFinalResponse: true });
            }
            throw e;
        }
    }

    public HandleError(errorCode: TranscriptionTelemetryErrorCodes): void {
      switch(errorCode) {
        case TranscriptionTelemetryErrorCodes.LostInternetConnectionReason:
          LogError(LogEventNames.Warning, "Live recording failure. " + TranscriptionTelemetryErrorCodes.ConnectionIssueFailureReason);

          this.Cancel();

          let transcriptionErrorEvent: ITranscriptionErrorEvent | undefined = { errorEventMessage: LOAD_TRANSCRIPTION_STRINGS_FAILURE_MESSAGE, errorEventName: LOAD_TRANSCRIPTION_STRINGS_FAILURE_MESSAGE};

          // TranscriptionStrings can be null if the user goes offline before TranscriptionStrings is loaded during boot.
          if (TranscriptionStrings != null) {
            transcriptionErrorEvent = { errorEventMessage: TranscriptionStrings.RecordingLostConnectionFailureReason, errorEventName: TranscriptionTelemetryErrorCodes.RecordingLostConnectionFailureReason };
          }

          // Navigate to the failure page.
          SetFailureStateAndRouteToFailure(
            LogEventNames.Warning,
            transcriptionErrorEvent!,
            FailureContinuation.RecoveryonInternetConnected,
            this.history);
      }
    }

    private CleanUpTaskState() {
        this.taskList = null;
        this.oneDriveUploadTask = null;
        this.session = null;
    }

    private async CancelAndClearBackup(shouldClearBackup: boolean): Promise<void> {
        try {
            if (this.taskList)
                this.taskList.Cancel();

            // Cancel any pending onedrive upload task
            if (this.oneDriveUploadTask != null)
                await this.oneDriveUploadTask.Cancel();

            // Close the live recording connection
            if (this.session != null) {
                if (shouldClearBackup) {
                    await this.session!.ClearBackup();
                    LogEvents.LiveRecording.End([{ key: LiveRecordingKeys.SuccessfullyEnded, value: true }]);
                }

                SetForegroundTaskInStore(null);
                this.session!.Close();
            }

            this.CleanUpTaskState();
        } catch (e) {
            this.CleanUpTaskState();
            LogEvents.ServiceLiveSpeechToText.AddResponse({ isSuccessful: false, message: `Failed when cancelling foreground task: ${ErrorStringFromException(e)}`, isFinalResponse: true });
            throw e;
        }
    }

    public async Cancel(): Promise<void> {
        this.isCancelledTask = true;
        return this.CancelAndClearBackup(true /*shouldClearBackup*/);
    }

    private async UploadFileAndInitiateTranscription(session: LiveRecordingSession, history: any) {
      const uploadResult: TaskListResult<ITranscriptionSourceFileProvider> = await this.ExecuteLiveRecordingUploadTask(session, TranscriptionStrings.FolderNameToUploadAudioFiles);
      // If the upload was canceled, we are done.
      if (uploadResult.status == TaskListResultStatus.Canceled) {
        return;
      }

      Dispatcher.dispatch({
        type: LoadingActionTypes.SetStatusAction,
        payload: LoadingStatus.TranscribingAudio
      });
      const sourceFile = await LoadTranscriptionSourceFileToStoreAndNavigateToTranscribingPage(uploadResult.result!, history, true /* isNewTranscript */, TranscribingEntryPoints.LIVE_RECORDING);

      //Show Transcription indicator on Host App
      if (HostAppSettings.IsSettingTrue(HostAppSettingName.AddTranscriptionIndicatorToHostEnabled)) {
        const addTranscriptionIndicatorToHostProvider: IAddTranscriptionIndicatorToHost = CreateAddTranscriptionIndicatorProvider();
        addTranscriptionIndicatorToHostProvider.AddTranscriptionIndicator(sourceFile!).catch((e) => {
          LogError(LogEventNames.StoredTranscriptionIndicator, "Error adding Transcription Indicator to Host " + ErrorStringFromException(e));
        });
      }
    }

    // Executes the upload tasks. This covers recording file upload, emptying browser-local cache and writing stored transcription to the doc.
    private async ExecuteLiveRecordingUploadTask(session: LiveRecordingSession | null, folderName: string)
        : Promise<TaskListResult<ITranscriptionSourceFileProvider>> {
        this.session = session;
        this.taskList = new TaskList<ITranscriptionSourceFileProvider | AudioFormat.IAudioFile | IOneDriveUploadTaskResult
        | {transcription: ITranscription, result: IOneDriveUploadTaskResult} | IStoredTranscription | void>([
            // Pause the live recording
            async (): Promise<void> =>
                session!.Pause(),

            // Get the audio file recording
            async (): Promise<AudioFormat.IAudioFile> =>
                session!.GetRecording(),

            // Log the duration of the audio file and upload to OneDrive
            async (audioFile: AudioFormat.IAudioFile): Promise<IOneDriveUploadTaskResult> => {
                LogEvents.LiveRecording.AddData([{ key: LiveRecordingKeys.AudioLengthSeconds, value: audioFile.duration }]);
                this.oneDriveUploadTask = new OneDriveUploadTask(this.authProvider, folderName, audioFile.file);
                return this.oneDriveUploadTask.UploadFile();
            },

            // Get the transcription
            async (result: IOneDriveUploadTaskResult): Promise<{transcription: ITranscription, result: IOneDriveUploadTaskResult}> => {
                const transcription = await session!.GetTranscript();
                return { transcription, result };
            },

            // Close the live recording session
            async (transcriptionAndResult: {transcription: ITranscription, result: IOneDriveUploadTaskResult}): Promise<IStoredTranscription> => {
                await session!.Close();
                await session!.ClearBackup();
                SetForegroundTaskInStore(null);
                this.session = session = null;

                LogEvents.LiveRecording.End([{ key: LiveRecordingKeys.SuccessfullyEnded, value: true }]);
                LogEvents.ServiceLiveSpeechToText.AddResponse({ isSuccessful: true, message: '', isFinalResponse: true});

                if (HostAppSettings.IsSettingTrue(HostAppSettingName.IsRecordingEventCaptureEnabled)) {
                  return {
                    storageType: GetSupportedTranscriptionStorageType(),
                    recording_time_stamps: RecordingEventManager.GetRecordingEventEpochTimeList(),
                    descriptor: {
                      transcription: transcriptionAndResult.transcription,
                      audioOneDriveItem: transcriptionAndResult.result.oneDriveItem,
                    },
                  };
                } else {
                  return {
                    storageType: GetSupportedTranscriptionStorageType(),
                    descriptor: {
                      transcription: transcriptionAndResult.transcription,
                      audioOneDriveItem: transcriptionAndResult.result.oneDriveItem,
                    },
                  };
                }
            },

            // Write stored transcription to document storage.
            async (storedTranscription: IStoredTranscription): Promise<ITranscriptionSourceFileProvider> => {
                await TranscriptionManagerFactory.GetInstance().WriteTranscriptionToStorage(storedTranscription);
                return CreateTranscriptionSourceFileProvider(storedTranscription, this.authProvider);
            }
        ]);

        const result = await this.taskList.ExecuteTaskListSequentially();
        if (result.status != TaskListResultStatus.Canceled) {
          this.CleanUpTaskState();
        }
        return <TaskListResult<ITranscriptionSourceFileProvider>>result;
    }
  }